import React, { useState, useEffect } from 'react';
import { X, Heart, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

const OnboardingModal = () => {
  const [isOpen, setIsOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user has seen the onboarding
    const hasSeenOnboarding = localStorage.getItem('has_seen_onboarding');
    if (!hasSeenOnboarding) {
      // Small delay for better UX
      setTimeout(() => {
        setIsOpen(true);
      }, 800);
    }
  }, []);

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleDismissPermanently = () => {
    localStorage.setItem('has_seen_onboarding', 'true');
    setIsOpen(false);
  };

  const handleLearnMore = () => {
    localStorage.setItem('has_seen_onboarding', 'true');
    setIsOpen(false);
    navigate('/safety-features');
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9998] flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-3xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto animate-fade-in">
        {/* Header */}
        <div className="relative bg-gradient-to-br from-[#E7C6C2] to-[#F7E5E5] p-8 rounded-t-3xl">
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 p-2 hover:bg-white/20 rounded-full transition-colors"
          >
            <X className="w-5 h-5 text-[#3E324D]" />
          </button>
          <div className="flex items-center gap-3 mb-3">
            <Heart className="w-10 h-10 text-[#6C4CA2]" fill="#6C4CA2" />
            <h2 className="text-3xl font-bold text-[#3E324D]">
              Welcome to SheSafe Haven
            </h2>
          </div>
          <p className="text-[#3E324D]/80 text-lg">
            A space designed with your safety and peace in mind.
          </p>
        </div>

        {/* Content */}
        <div className="p-8">
          <div className="mb-6">
            <p className="text-[#6E6275] text-base leading-relaxed mb-4">
              Before you begin your journey, there's an important safety feature you should know about.
            </p>
          </div>

          {/* Emergency Exit Feature */}
          <div className="bg-[#F7F5F8] rounded-2xl p-6 mb-6 border-2 border-[#E7C6C2]">
            <div className="flex items-start gap-4 mb-4">
              <div className="bg-[#6C4CA2] p-3 rounded-xl">
                <Shield className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#3E324D] mb-2">
                  Emergency Exit Feature
                </h3>
                <p className="text-[#6E6275] text-sm">
                  Double-tap the SheSafe Haven logo to instantly mask the app
                </p>
              </div>
            </div>

            <div className="space-y-3 text-[#6E6275] text-sm">
              <div className="flex items-start gap-2">
                <span className="text-[#6C4CA2] font-bold text-lg">•</span>
                <p>
                  <strong>Quick activation:</strong> Look for the <strong className="text-red-600">red exit button</strong> in the top-right corner. One click activates emergency exit.
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#6C4CA2] font-bold text-lg">•</span>
                <p>
                  <strong>Instant camouflage:</strong> The app becomes a neutral "Daily Focus" 
                  planner that looks like an ordinary wellness tool
                </p>
              </div>
              <div className="flex items-start gap-2">
                <span className="text-[#6C4CA2] font-bold text-lg">•</span>
                <p>
                  <strong>Easy return:</strong> Tap the Settings ⚙️ icon or wait 60 seconds 
                  to return to the app
                </p>
              </div>
            </div>

            <div className="mt-4 bg-white p-4 rounded-lg border border-[#E7C6C2]">
              <p className="text-[#3E324D] text-sm font-semibold flex items-center gap-2">
                <span className="text-red-500 text-xl">🔴</span>
                The red exit button appears on every page after you log in!
              </p>
            </div>
          </div>

          <div className="bg-[#6C4CA2]/10 rounded-xl p-4 mb-6">
            <p className="text-[#3E324D] text-sm italic">
              "Your safety is paramount. This feature ensures you can use SheSafe Haven 
              with confidence, knowing you have a discreet exit anytime you need it."
            </p>
            <p className="text-[#3E324D] text-sm font-semibold mt-2">— Angie</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col sm:flex-row gap-3">
            <Button
              onClick={handleLearnMore}
              className="flex-1 bg-[#6C4CA2] hover:bg-[#8B6BB8] text-white py-6 text-base rounded-xl"
            >
              Learn More About Safety
            </Button>
            <Button
              onClick={handleDismissPermanently}
              className="flex-1 bg-[#E7C6C2] hover:bg-[#F7E5E5] text-[#3E324D] py-6 text-base rounded-xl"
            >
              Got It, Don't Show Again
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingModal;
